import { Injectable } from '@angular/core';
import { db } from 'src/app/db/db';
// models
import { PendingAnswers } from 'src/app/models/pending-answers';
import { TemporaryAnswers } from 'src/app/models/temporary-answers';
import { DurationAuditRecord } from '../models/duration-validation';
// services
import { QservusApiService } from './qservus-api.service';
// config
import { StorageTTLConfig } from '../config/storage-ttl-config';

@Injectable({
  providedIn: 'root'
})
export class DbService {

  public isDBOpen: boolean = false;
  private dbReadyPromise: Promise<void> | null = null;

  constructor(
    private qservusService: QservusApiService) {
    db.db_open.subscribe((next) => {
      this.isDBOpen = next
      // if (!next) this.snack.openSnack(this.transPipe.transform('SURVEY.DB_IS_CLOSED'), this.transPipe.transform('COMMON.OK'), 'right', 'top', ['snack-qservus-blue'], 5000);

    })
  }

  /**
   * Waits until Dexie finishes initializing (or times out) so consumers don't query before the DB is ready.
   */
  waitUntilReady(): Promise<void> {
    if (this.isDBOpen) return Promise.resolve();

    if (!this.dbReadyPromise) {
      this.dbReadyPromise = db.ensureInitialized()
        .then(() => {
          this.isDBOpen = true;
        })
        .catch((error) => {
          console.warn('IndexedDB failed to open, continuing without it:', error);
        })
        .finally(() => {
          this.dbReadyPromise = null;
        });
    }

    return this.dbReadyPromise;
  }

  // PENDING ANSWERS (OFFLINE MODE)
  async getPendingAnswers(su_survey_id: number) {
    await this.waitUntilReady();
    return db._getByKey('pendingAnswers', 'su_survey_id', su_survey_id);
  }
  async pushPendingAnswers(obj: PendingAnswers) {
    await this.waitUntilReady();
    obj.status = 0;
    // Set expiration using centralized TTL configuration
    if (!obj.expiresAt) {
      obj.expiresAt = StorageTTLConfig.createExpirationTimestamp(StorageTTLConfig.PENDING_ANSWERS_TTL);
    }
    return db._add('pendingAnswers', obj);
  }
  async updatePendingAnswers(id: number, obj: PendingAnswers) {
    await this.waitUntilReady();
    return db._update('pendingAnswers', id, obj);
  }
  async deletePendingAnswer(id_pending_answer: number) {
    await this.waitUntilReady();
    return db._delete('pendingAnswers', id_pending_answer);
  }
  async deletePendingAnswerBySurveyId(su_survey_id: number) {
    await this.waitUntilReady();
    return db._deleteByKey('pendingAnswers', 'su_survey_id', su_survey_id);
  }
  
  // TEMPORARY ANSWERS METHODS (WHILE ANSWERING...)
  async getTemporaryAnswersBySurveyUUID() {
    await this.waitUntilReady();
    return db._getByKey('temporaryAnswers', 'survey_uuid', this.qservusService.uuid4);
  }
  async getTemporaryAnswersByProperty(property: string, value: string|number) {
    await this.waitUntilReady();
    return db._getByKey('temporaryAnswers', property, value);
  }
  async pushTemporaryAnswer(obj: TemporaryAnswers) {
    await this.waitUntilReady();
    obj.survey_uuid = this.qservusService.uuid4;
    // Set expiration using centralized TTL configuration (shorter for temporary data)
    if (!obj.expiresAt) {
      obj.expiresAt = StorageTTLConfig.createExpirationTimestamp(StorageTTLConfig.TEMPORARY_ANSWERS_TTL);
    }
    return db._add('temporaryAnswers', obj);
  }
  async updateTemporaryAnswer(obj: TemporaryAnswers) {
    await this.waitUntilReady();
    return db._update('temporaryAnswers', obj.id, obj);
  }
  async deleteTemporaryAnswersBySurveyUUID() {
    await this.waitUntilReady();
    return db._deleteByKey('temporaryAnswers', 'survey_uuid', this.qservusService.uuid4);
  }
  
  /**
   * Clean up expired pending answers
   */
  async cleanupExpiredPendingAnswers(): Promise<number> {
    try {
      await this.waitUntilReady();
      if (!db.isOpen()) return 0;
      
      const now = Date.now();
      const deleteCount = await db.pendingAnswers
        .where('expiresAt')
        .below(now)
        .delete();
        
      if (deleteCount > 0) {
        // console.log(`Cleaned up ${deleteCount} expired pending answers`);
      }
      
      return deleteCount;
    } catch (error) {
      console.warn('Error cleaning expired pending answers:', error);
      return 0;
    }
  }
  
  /**
   * Clean up expired temporary answers
   */
  async cleanupExpiredTemporaryAnswers(): Promise<number> {
    try {
      await this.waitUntilReady();
      if (!db.isOpen()) return 0;
      
      const now = Date.now();
      const deleteCount = await db.temporaryAnswers
        .where('expiresAt')
        .below(now)
        .delete();
        
      if (deleteCount > 0) {
        // console.log(`Cleaned up ${deleteCount} expired temporary answers`);
      }
      
      return deleteCount;
    } catch (error) {
      console.warn('Error cleaning expired temporary answers:', error);
      return 0;
    }
  }

  // DURATION AUDIT RECORDS
  async pushDurationAudit(record: DurationAuditRecord) {
    await this.waitUntilReady();
    record.created_at = record.created_at ?? Date.now();
    record.synced = record.synced ?? 0;
    if (!record.expiresAt) record.expiresAt = StorageTTLConfig.createExpirationTimestamp(StorageTTLConfig.DURATION_AUDIT_TTL);
    return db.durationAudits.add(record);
  }

  async getPendingDurationAudits() {
    await this.waitUntilReady();
    if (!db.isOpen()) return [];
    return db.durationAudits.where('synced').equals(0).toArray();
  }

  async markDurationAuditSynced(id: number) {
    await this.waitUntilReady();
    if (!db.isOpen()) return null;
    return db.durationAudits.update(id, { synced: 1 });
  }

  async deleteDurationAudit(id: number) {
    await this.waitUntilReady();
    if (!db.isOpen()) return null;
    return db.durationAudits.delete(id);
  }

  async cleanupExpiredDurationAudits(): Promise<number> {
    try {
      await this.waitUntilReady();
      if (!db.isOpen()) return 0;
      const now = Date.now();
      return await db.durationAudits.where('expiresAt').below(now).delete();
    } catch (error) {
      console.warn('Error cleaning expired duration audits:', error);
      return 0;
    }
  }
}

